import { styled } from 'goober';
import type { KeyboardEvent, MouseEvent } from 'react';

import { prepareLink } from '@/utils/link';

import { Icon, type IconVariant } from '../icons/icon';
import { Tooltip } from '../tooltip/tooltip';

export interface LinkIconProps {
  url: string;
  icon: IconVariant;
  size?: string;
  disableTooltip?: boolean;
  dataTestId?: string;
  color?: string;
  stopPropagation?: boolean;
  className?: string;
}

export const LinkIcon = ({
  url,
  icon,
  size,
  disableTooltip,
  dataTestId,
  color,
  stopPropagation,
  className,
}: LinkIconProps) => {
  const href = prepareLink(url);

  const openLink = (event: MouseEvent | KeyboardEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLink(event);
    }
  };

  return (
    <LinkButton
      role="link"
      aria-disabled={href ? undefined : true}
      aria-label={href || undefined}
      data-url={href}
      tabIndex={href ? 0 : -1}
      size={size}
      data-testid={dataTestId}
      onClick={openLink}
      onKeyDown={handleKeyDown}
      className={className}
    >
      <Tooltip disabled={disableTooltip} content={url} maxWidth="unset">
        <Icon icon={icon} color={color} />
      </Tooltip>
    </LinkButton>
  );
};

const LinkButton = styled('span')<{ size?: string }>`
  display: inline-block;
  width: ${({ size }) => size ?? '18px'};
  height: ${({ size }) => size ?? '18px'};
  cursor: pointer;

  &[aria-disabled='true'] {
    cursor: default;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;
